import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { BrowserRouter, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import { LangProvider } from './context/LangContext';
import { CartProvider } from './context/CartContext';
import { SearchProvider } from './context/SearchContext';
import { SessionProvider, useSession } from './context/SessionContext';
import { CatalogProvider } from './context/CatalogContext';
import { createSecureAxios, secureStorage } from './utils/security';

import AppShell from './components/AppShell';
import HomePage from './pages/HomePage';
import CategoriesPage from './pages/CategoriesPage';
import ProductDetailPage from './pages/ProductDetailPage';
import ProductDetailOverlay from './pages/ProductDetailOverlay';
import CartPage from './pages/CartPage';
import CartOverlay from './pages/CartOverlay';
import CheckoutPage from './pages/CheckoutPage';
import OrdersPage from './pages/OrdersPage';
import OrderDetailPage from './pages/OrderDetailPage';
import ProfilePage from './pages/ProfilePage';
import AddressesPage from './pages/AddressesPage';
import WalletPage from './pages/WalletPage';
import SignupPage from './pages/SignupPage';

const API = (process.env.REACT_APP_API_URL || 'https://nrkmart-api.vercel.app').replace(/\/$/, '');
createSecureAxios(axios, API);

const SESSION_KEY = 'nrk_user';
function loadSession() { try { return secureStorage.get(SESSION_KEY); } catch { return null; } }
function saveSession(user) {
  try {
    secureStorage.set(SESSION_KEY, { phone: user.phone, name: user.name || '', email: user.email || '', token: user.token || null });
  } catch { /* ignore */ }
}
function clearSession() { try { secureStorage.remove(SESSION_KEY); } catch { /* ignore */ } }

function LoadingScreen() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-paper">
      <div className="text-center">
        <div className="text-4xl">🛒</div>
        <div className="font-extrabold text-brand-dark mt-2.5">Loading NRK Mart…</div>
      </div>
    </div>
  );
}

function MaintenanceScreen({ maintenance }) {
  const until = maintenance.until ? new Date(maintenance.until) : null;
  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-gradient-to-br from-brand-dark to-brand">
      <div className="bg-white rounded-3xl px-7 py-10 max-w-sm w-full text-center shadow-lift">
        <div className="text-5xl mb-3">🔧</div>
        <div className="font-black text-xl text-brand-dark mb-2.5">Under Maintenance</div>
        <div className="text-sm text-ink-soft leading-relaxed">{maintenance.message}</div>
        {until ? (
          <div className="mt-4 bg-brand-mint rounded-xl px-4 py-3">
            <div className="text-[11px] font-extrabold text-ink-faint uppercase mb-1">Back online at</div>
            <div className="font-black text-brand-dark">{until.toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}</div>
          </div>
        ) : null}
        <div className="mt-5 text-xs text-ink-faint">For urgent orders call or WhatsApp us directly.</div>
      </div>
    </div>
  );
}

/** Resolves ?rzp_return / ?cf_return from Razorpay/Cashfree redirects, on any load. */
function PaymentReturnResolver() {
  const navigate = useNavigate();
  const { onAuth, sessionUser } = useSession();

  useEffect(() => {
    const p = new URLSearchParams(window.location.search);

    if (p.get('cf_return') === '1') {
      (async () => {
        const orderId = p.get('order_id') || '';
        let pending = null;
        try { pending = JSON.parse(sessionStorage.getItem('nrk_cf_pending') || 'null'); } catch { pending = null; }
        const id = orderId || pending?.order_id || pending?.order_number;
        if (!id) return;
        try {
          const res = await axios.get(`${API}/api/payments/cashfree/verify`, { params: { order_id: id } });
          const paid = !!res.data?.paid;
          const result = {
            order_id: res.data?.order_id || pending?.order_id,
            order_number: res.data?.order_number || pending?.order_number || orderId,
            payment_method: 'cashfree',
            payment_status: paid ? 'verified' : 'pending',
            amount_due: pending?.amount_due,
            total_amount: pending?.amount_due,
          };
          try { sessionStorage.removeItem('nrk_cf_pending'); } catch { /* ignore */ }
          if (pending?.fullPhone && !loadSession()) { const u = { phone: pending.fullPhone, name: pending.name || '' }; saveSession(u); onAuth(u); }
          navigate('/', { replace: true, state: { placedResult: result } });
        } catch {
          if (pending) navigate('/', { replace: true, state: { placedResult: { order_id: pending.order_id, order_number: pending.order_number, payment_method: 'cashfree', payment_status: 'pending' } } });
        }
      })();
      return;
    }

    if (p.get('rzp_return') === '1') {
      (async () => {
        const orderId = p.get('order_id') || '';
        const rzpOrderIdParam = p.get('rzp_order_id') || '';
        let pending = null;
        try { pending = JSON.parse(sessionStorage.getItem('nrk_rzp_pending') || 'null'); } catch { pending = null; }
        const id = orderId || pending?.order_id || pending?.order_number;
        const rzpId = rzpOrderIdParam || pending?.razorpay_order_id;
        if (!id && !rzpId) return;
        try {
          const params = id ? { order_id: id } : { rzp_order_id: rzpId };
          const res = await axios.get(`${API}/api/payments/razorpay/verify`, { params });
          const paid = !!res.data?.paid;
          if (paid) {
            try { sessionStorage.removeItem('nrk_rzp_pending'); } catch { /* ignore */ }
            if (pending?.fullPhone && !loadSession()) { const u = { phone: pending.fullPhone, name: pending.name || '', token: pending.token }; saveSession(u); onAuth(u); }
            const result = {
              order_id: res.data?.order_id || pending?.order_id,
              order_number: res.data?.order_number || pending?.order_number || orderId,
              payment_method: 'razorpay',
              payment_status: 'verified',
              amount_due: pending?.amount_due,
              total_amount: pending?.amount_due,
            };
            navigate('/', { replace: true, state: { placedResult: result } });
          } else {
            // No order was ever created for this attempt — nothing to show as
            // "placed". Send the customer back to checkout so cart is intact
            // and they can retry, instead of inventing a fake pending order.
            navigate('/checkout', { replace: true });
          }
        } catch {
          navigate('/checkout', { replace: true });
        }
      })();
    }
  }, []);

  return null;
}

function RootRoutes() {
  const location = useLocation();
  const backgroundLocation = location.state?.backgroundLocation;

  return (
    <>
      <PaymentReturnResolver />
      <Routes location={backgroundLocation || location}>
        <Route element={<AppShell />}>
          <Route index element={<HomePage />} />
          <Route path="/categories" element={<CategoriesPage />} />
          <Route path="/product/:id" element={<ProductDetailPage />} />
          <Route path="/cart" element={<CartPage />} />
          <Route path="/checkout" element={<CheckoutPage />} />
          <Route path="/orders" element={<OrdersPage />} />
          <Route path="/orders/:id" element={<OrderDetailPage />} />
          <Route path="/profile" element={<ProfilePage />} />
          <Route path="/profile/addresses" element={<AddressesPage />} />
          <Route path="/profile/wallet" element={<WalletPage />} />
          <Route path="/signup" element={<SignupPage />} />
        </Route>
      </Routes>
      {backgroundLocation ? (
        <Routes>
          <Route path="/product/:id" element={<ProductDetailOverlay />} />
          <Route path="/cart" element={<CartOverlay />} />
        </Routes>
      ) : null}
    </>
  );
}

export default function App() {
  const params = new URLSearchParams(window.location.search);
  const urlPhone = params.get('phone') || '';
  const urlName = params.get('name') || '';
  const urlLang = params.get('lang') || '';
  const urlLat = params.get('lat') || '';
  const urlLng = params.get('lng') || '';
  const urlRef = params.get('ref') || '';
  const fromWhatsApp = !!urlPhone;

  if (urlRef) {
    try { sessionStorage.setItem('nrk_ref', String(urlRef).trim().toUpperCase()); } catch { /* ignore */ }
  }

  const [sessionUser, setSessionUser] = useState(() => (urlPhone ? { phone: urlPhone, name: urlName } : loadSession()));
  const [maintenance, setMaintenance] = useState(null);

  useEffect(() => {
    const controller = new AbortController();
    const timer = setTimeout(() => { controller.abort(); setMaintenance(false); }, 5000);
    axios.get(`${API}/health`, { signal: controller.signal })
      .then(() => { clearTimeout(timer); setMaintenance(false); })
      .catch((err) => {
        clearTimeout(timer);
        const data = err.response?.data;
        setMaintenance(data?.maintenance ? data : false);
      });
    return () => { clearTimeout(timer); controller.abort(); };
  }, []);

  const handleAuth = (user) => { saveSession(user); setSessionUser(user); };
  const handleLogout = () => { clearSession(); setSessionUser(null); };

  if (maintenance === null) return <LoadingScreen />;
  if (maintenance && maintenance.maintenance) return <MaintenanceScreen maintenance={maintenance} />;

  return (
    <LangProvider initialLang={urlLang || undefined}>
      <CartProvider>
        <SearchProvider>
          <SessionProvider value={{ sessionUser, onAuth: handleAuth, onLogout: handleLogout, phone: urlPhone, name: urlName, fromWhatsApp }}>
            <CatalogProvider lat={urlLat ? parseFloat(urlLat) : null} lng={urlLng ? parseFloat(urlLng) : null}>
              <BrowserRouter>
                <RootRoutes />
              </BrowserRouter>
            </CatalogProvider>
          </SessionProvider>
        </SearchProvider>
      </CartProvider>
    </LangProvider>
  );
}
